/**
 * store/actions/index.js
 *
 * Defines the available store actions.
 *
 * Actions can have an 'effect' function which acts like the useEffect Hook in that it is
 * executed prior being delegated to its reducer.  If effect() return a truthy value the
 * action is cancelled and the store state is not affected.
 *
 */

import * as types from './types';
import initialState from "../store/initial-state";
import {AccountService} from "../../services";


/**
 * setUser updates the store's user object e.g. after login.
 *
 * @param user
 * @returns {{payload: *, type: string}}
 */
export const setUser = user => ({
    type: types.SET_USER,
    payload: user
});

/**
 * clearUser removes the store's user object eg. after logout
 *
 * @returns {{payload: {}, type: string}}
 */
export const clearUser = () => ({
    type: types.CLEAR_USER,
    payload: {...initialState.user}
});

/**
 * setEndpoint updates the store's endpoint object.
 *
 * @param endpoint
 * @returns {{payload: *, type: string}}
 */
export const setEndpoint = endpoint => ({
    type: types.SET_ENDPOINT,
    payload: endpoint
});

/**
 * clearEndpoint removes the store's endpoint object eg. after deactivation
 *
 * @returns {{payload: {}, type: string}}
 */
export const clearEndpoint = () => ({
    type: types.CLEAR_ENDPOINT,
    payload: {...initialState.endpoint}
});

/**
 * setAccount updates the store's account object
 *
 * @param account
 * @returns {{payload: *, type: string}}
 */
export const setAccount = account => ({
    type: types.SET_ACCOUNT,
    payload: account
});

/**
 * clearAccount resets the store' account object
 * @param account
 * @returns {{payload: {blockeds: [], dialActions: [], isFetching: boolean, didInvalidate: boolean, id: number, greetings: [], voicemails: [], services: [], error: null, contacts: [], users: []}, type: string}}
 */
export const clearAccount = account => ({
    type: types.CLEAR_ACCOUNT,
    payload: {...initialState.account}
});

/**
 * fetchAccount fetches the account from the server asynchronously if the account
 * has not yet been set or have been invalidated.
 *
 * @param id
 * @param state
 * @param dispatch
 * @returns {{payload: *, effect: (function(): boolean), type: string}}
 */
export const fetchAccount = (id, state, dispatch) => ({
    type: types.FETCH_ACCOUNT,
    payload: id,
    effect: () => fetchAccountIfNeeded(id, state, dispatch)
});

/**
 * receiveAccount updates the account fetched from the server.
 *
 * @param account
 * @returns {{payload: *, type: string, receivedAt: number}}
 */
export const receiveAccount = account => ({
    type: types.RECEIVE_ACCOUNT,
    payload: account,
    receivedAt: Date.now()
});

/**
 * fetchAccountError updates the store's account with an API error encountered by the last fetch.
 *
 * @param error
 * @returns {{payload: *, type: string}}
 */
export const fetchAccountError = error => ({
    type: types.FETCH_ACCOUNT_ERROR,
    payload: error
});

/**
 * invalidateAccount invalidates the account object so it will be reloaded by the next fetchAccount action
 * @returns {{payload: {}, type: string}}
 */
export const invalidateAccount = () => ({
    type: types.INVALIDATE_ACCOUNT,
    payload: {}
});

export const setAccountPaymentMethod = paymentMethod => ({
    type: types.SET_ACCOUNT_PAYMENT_METHOD,
    payload: paymentMethod,
})

export const setServices = services => ({
    type: types.SET_SERVICES,
    payload: services,
})

// fetchAccountIfNeeded fetches the account from the server if it has not been loaded yet or has been invalidated.
const fetchAccountIfNeeded = (id, state, dispatch) => {
    if (id !==0 && shouldFetchAccount(state)) {
        AccountService.get(id)
            .then(account => dispatch(receiveAccount(account)))
            .catch(error => dispatch(fetchAccountError(error)))
        return false
    }
    return true
}

// shouldFetchAccount returns true if the account needs to be fetched from the server.
const shouldFetchAccount = (state) => {
    const account = state.account
    if (account.isFetching){
        return false
    } else if (!account.id) {
        return true
    } else {
        return account.didInvalidate
    }
}

